import { useEffect, useMemo, useState } from 'react';
import { Modal } from '@/components/primitives/Modal';
import { Button } from '@/components/primitives/Button';
import { useFormats, useTemplateInFormat, useTemplatesForFormat } from '@/hooks/useTemplates';
import { storedFormatIdFromPath, CUSTOM_FORMAT_PATH_ID } from '@/services/formats';
import { createCreative } from '@/services/creatives';
import type { Creative, TemplateSummary } from '@/types/api';
import { TemplatePreview } from '@/components/app/TemplatePreview';
import {
  DESIGN_CANVAS_HEIGHT_PX,
  DESIGN_CANVAS_WIDTH_PX,
} from '@/creativeComposition/elementMerge';

const FORMAT_ICONS: Record<string, string> = {
  standard: 'rectangle',
  animation: 'movie',
  carousel: 'view_carousel',
  multi: 'groups',
  interaction: 'touch_app',
  ar: 'view_in_ar',
  custom: 'palette',
};

interface NewCreativeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (creative: Creative) => void;
  initialTemplateId?: string | null;
  initialFormatId?: string | null;
  campaignId?: number;
}

export function NewCreativeModal({
  isOpen,
  onClose,
  onCreated,
  initialTemplateId,
  initialFormatId,
  campaignId,
}: NewCreativeModalProps) {
  const { data: formats, loading: formatsLoading, error: formatsError } = useFormats();
  const formatList = useMemo(() => formats ?? [], [formats]);

  const [pickerStep, setPickerStep] = useState<1 | 2>(1);
  const [selectedFormatPathId, setSelectedFormatPathId] = useState<string | null>(null);
  const { data: templates, loading: templatesLoading, error: templatesError } =
    useTemplatesForFormat(selectedFormatPathId);

  const [name, setName] = useState('');
  const [durationSeconds, setDurationSeconds] = useState<string>('60');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const templateDetail = useTemplateInFormat(selectedFormatPathId, selectedTemplateId);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const allTemplates = useMemo(() => templates ?? [], [templates]);

  const selectedTemplateSummary = useMemo(
    () => allTemplates.find((t) => t.id === selectedTemplateId) ?? null,
    [allTemplates, selectedTemplateId],
  );

  const previewTemplate = templateDetail.data;
  const previewWidth = Number(previewTemplate?.render_settings?.width_px ?? DESIGN_CANVAS_WIDTH_PX);
  const previewHeight = Number(
    previewTemplate?.render_settings?.height_px ?? DESIGN_CANVAS_HEIGHT_PX,
  );
  const previewAspect =
    Number.isFinite(previewWidth) && Number.isFinite(previewHeight) && previewHeight > 0
      ? `${previewWidth} / ${previewHeight}`
      : `${DESIGN_CANVAS_WIDTH_PX} / ${DESIGN_CANVAS_HEIGHT_PX}`;

  useEffect(() => {
    if (!isOpen) return;
    setName('');
    setSubmitError('');
    if (initialTemplateId && initialFormatId) {
      setPickerStep(2);
      setSelectedFormatPathId(initialFormatId);
      setSelectedTemplateId(initialTemplateId);
      return;
    }
    setPickerStep(1);
    setSelectedFormatPathId(initialFormatId ?? null);
    setSelectedTemplateId(initialTemplateId ?? null);
  }, [isOpen, initialTemplateId, initialFormatId]);

  useEffect(() => {
    if (!isOpen || pickerStep !== 1) return;
    if (selectedFormatPathId != null) return;
    if (formatList.length === 0) return;
    const fallback =
      initialFormatId && formatList.some((f) => f.id === initialFormatId)
        ? initialFormatId
        : formatList[0].id;
    setSelectedFormatPathId(fallback);
  }, [formatList, initialFormatId, isOpen, pickerStep, selectedFormatPathId]);

  useEffect(() => {
    if (!isOpen || pickerStep !== 2) return;
    if (selectedTemplateId) return;
    if (allTemplates.length === 0) return;
    const fallback =
      initialTemplateId && allTemplates.some((t) => t.id === initialTemplateId)
        ? initialTemplateId
        : allTemplates[0].id;
    setSelectedTemplateId(fallback);
  }, [allTemplates, initialTemplateId, isOpen, pickerStep, selectedTemplateId]);

  const handleClose = () => {
    if (!submitting) onClose();
  };

  async function handleCreate() {
    if (!name.trim() || !selectedTemplateId || !selectedFormatPathId) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      const created = await createCreative({
        name: name.trim(),
        format_id: storedFormatIdFromPath(selectedFormatPathId),
        template_id: selectedTemplateId,
        render_duration_ms: durationSeconds ? parseInt(durationSeconds, 10) * 1000 : 60000,
        assigned_campaign_ids: campaignId != null ? [campaignId] : [],
      });
      onCreated?.(created);
      onClose();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to create creative');
    } finally {
      setSubmitting(false);
    }
  }

  const error = formatsError || (pickerStep === 2 ? templatesError : null);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="New Creative"
      subtitle={
        pickerStep === 1
          ? 'Step 1: choose a format (built-in or your organization custom bucket).' : 'Step 2: name your creative and pick a template.'
      }
      width="max-w-6xl"
      footer={
        pickerStep === 1 ? (
          <Button
            onClick={() => setPickerStep(2)}
            disabled={submitting || formatsLoading || !selectedFormatPathId}
            className="aspectly:w-full aspectly:py-4 aspectly:text-base aspectly:shadow-lg aspectly:shadow-primary/20 aspectly:justify-center"
          >
            Next: Choose template
          </Button>
        ) : (
          <Button
            onClick={handleCreate}
            disabled={
              submitting || templatesLoading || !name.trim() || !selectedTemplateId || !selectedFormatPathId
            }
            className="aspectly:w-full aspectly:py-4 aspectly:text-base aspectly:shadow-lg aspectly:shadow-primary/20 aspectly:justify-center"
          >
            {submitting ? 'Creating...' : 'Create'}
          </Button>
        )
      }
    >
      <div className="aspectly:flex aspectly:flex-col aspectly:gap-6">
        {submitError && (
          <div className="aspectly:p-4 aspectly:rounded-xl aspectly:bg-error-container aspectly:text-on-error-container aspectly:text-sm">
            {submitError}
          </div>
        )}
        {error && (
          <div className="aspectly:p-4 aspectly:rounded-xl aspectly:bg-error-container aspectly:text-on-error-container aspectly:text-sm">
            {error}
          </div>
        )}

        {pickerStep === 1 && (
          <div className="aspectly:flex aspectly:flex-col aspectly:gap-4">
            <p className="aspectly:text-[10px] aspectly:font-bold aspectly:text-on-surface-variant aspectly:uppercase aspectly:tracking-widest">
              Available formats
            </p>
            {formatsLoading && (
              <div className="aspectly:grid aspectly:grid-cols-2 aspectly:sm:grid-cols-3 aspectly:gap-3">
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="aspectly:h-24 aspectly:bg-surface-container-lowest aspectly:rounded-xl aspectly:border aspectly:border-outline-variant/10 aspectly:animate-pulse"
                  />
                ))}
              </div>
            )}
            {!formatsLoading && formatList.length === 0 && (
              <p className="aspectly:text-sm aspectly:text-on-surface-variant">No formats available.</p>
            )}
            {!formatsLoading && formatList.length > 0 && (
              <div className="aspectly:grid aspectly:grid-cols-2 aspectly:sm:grid-cols-3 aspectly:gap-3">
                {formatList.map((f) => {
                  const isSelected = selectedFormatPathId === f.id;
                  const icon = FORMAT_ICONS[f.id] ?? 'view_quilt';
                  const isCustom = f.id === CUSTOM_FORMAT_PATH_ID;
                  return (
                    <button
                      key={f.id}
                      type="button"
                      onClick={() => setSelectedFormatPathId(f.id)}
                      className={`aspectly:flex aspectly:flex-col aspectly:items-start aspectly:gap-2 aspectly:p-4 aspectly:rounded-xl aspectly:text-left aspectly:transition-all ${isSelected
                          ? 'aspectly:bg-primary aspectly:text-on-primary aspectly:shadow-md aspectly:shadow-primary/20 aspectly:ring-2 aspectly:ring-primary/30' : 'aspectly:bg-surface-container-lowest aspectly:text-on-surface aspectly:hover:bg-primary-container'
                        }`}
                    >
                      <span className="material-symbols-outlined aspectly:text-2xl">{icon}</span>
                      <span className="aspectly:text-sm aspectly:font-bold aspectly:leading-tight">{f.name}</span>
                      {isCustom && (
                        <span className="aspectly:text-[9px] aspectly:uppercase aspectly:font-black aspectly:tracking-widest aspectly:opacity-80">
                          Custom
                        </span>
                      )}
                      <span className="aspectly:text-[11px] aspectly:opacity-80 aspectly:line-clamp-2">{f.description}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {pickerStep === 2 && (
          <div className="aspectly:grid aspectly:grid-cols-1 aspectly:lg:grid-cols-12 aspectly:gap-8">
            <div className="aspectly:col-span-1 aspectly:lg:col-span-5 aspectly:flex aspectly:flex-col aspectly:gap-6">
              <button
                type="button"
                className="aspectly:text-xs aspectly:font-bold aspectly:text-primary aspectly:uppercase aspectly:tracking-widest aspectly:self-start aspectly:hover:underline"
                onClick={() => setPickerStep(1)}
                disabled={submitting}
              >
                ← Change format
              </button>

              <div className="aspectly:flex aspectly:flex-col aspectly:gap-2">
                <label
                  className="aspectly:text-[10px] aspectly:uppercase aspectly:font-bold aspectly:tracking-widest aspectly:text-on-surface-variant aspectly:block"
                  htmlFor="creative-name"
                >
                  Creative Name
                </label>
                <input
                  id="creative-name"
                  className="aspectly:w-full aspectly:bg-surface-container-low aspectly:border aspectly:border-outline-variant/20 aspectly:rounded-xl aspectly:px-4 aspectly:py-3 aspectly:text-sm aspectly:font-bold aspectly:focus:outline-hidden aspectly:focus:ring-2 aspectly:focus:ring-primary/20 aspectly:focus:border-primary aspectly:transition-all aspectly:placeholder:text-on-surface-variant/40 aspectly:placeholder:font-normal"
                  placeholder="Summer Brand Campaign"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={submitting}
                />
              </div>

              <div className="aspectly:flex aspectly:flex-col aspectly:gap-2">
                <label
                  className="aspectly:text-[10px] aspectly:uppercase aspectly:font-bold aspectly:tracking-widest aspectly:text-on-surface-variant aspectly:block"
                  htmlFor="creative-length"
                >
                  Creative Length (Seconds)
                </label>
                <input
                  id="creative-length"
                  className="aspectly:w-full aspectly:bg-surface-container-low aspectly:border aspectly:border-outline-variant/20 aspectly:rounded-xl aspectly:px-4 aspectly:py-3 aspectly:text-sm aspectly:font-bold aspectly:focus:outline-hidden aspectly:focus:ring-2 aspectly:focus:ring-primary/20 aspectly:focus:border-primary aspectly:transition-all aspectly:placeholder:text-on-surface-variant/40 aspectly:placeholder:font-normal"
                  placeholder="60"
                  type="number"
                  value={durationSeconds}
                  onChange={(e) => setDurationSeconds(e.target.value)}
                  disabled={submitting}
                  min={1}
                />
              </div>

              <div className="aspectly:flex aspectly:flex-col aspectly:gap-4">
                <h2 className="aspectly:text-lg aspectly:font-bold aspectly:font-headline aspectly:text-on-surface">Templates</h2>
                {templatesLoading && (
                  <div className="aspectly:space-y-3">
                    {[1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className="aspectly:h-20 aspectly:bg-surface-container-lowest aspectly:rounded-xl aspectly:border aspectly:border-outline-variant/10 aspectly:animate-pulse"
                      />
                    ))}
                  </div>
                )}
                {!templatesLoading && allTemplates.length === 0 && (
                  <p className="aspectly:text-sm aspectly:text-on-surface-variant">No templates in this format.</p>
                )}
                {!templatesLoading && allTemplates.length > 0 && (
                  <div className="aspectly:space-y-3 aspectly:max-h-[350px] aspectly:overflow-y-auto aspectly:pr-2 aspectly:custom-scrollbar">
                    {allTemplates.map((t: TemplateSummary) => {
                      const isSelected = selectedTemplateId === t.id;
                      const isCustomFormat = t.format_id.startsWith('custom-');
                      return (
                        <div
                          key={t.id}
                          onClick={() => setSelectedTemplateId(t.id)}
                          className={`aspectly:p-4 aspectly:rounded-xl aspectly:flex aspectly:items-start aspectly:gap-4 aspectly:cursor-pointer aspectly:transition-all ${isSelected
                              ? 'aspectly:bg-primary-container aspectly:border-l-4 aspectly:border-primary' : 'aspectly:bg-surface-container-lowest aspectly:hover:bg-primary-container aspectly:border-l-4 aspectly:border-transparent aspectly:hover:border-primary-container/30'
                            }`}
                        >
                          <div
                            className={`aspectly:w-10 aspectly:h-10 aspectly:rounded-lg aspectly:flex aspectly:items-center aspectly:justify-center aspectly:shrink-0 ${isSelected ? 'aspectly:bg-primary' : 'aspectly:bg-surface-container'}`}
                          >
                            <span
                              className={`material-symbols-outlined aspectly:text-xl ${isSelected ? 'aspectly:text-on-primary' : 'aspectly:text-on-surface-variant'}`}
                            >
                              tv
                            </span>
                          </div>
                          <div className="aspectly:flex-1 aspectly:min-w-0">
                            <div className="aspectly:flex aspectly:items-center aspectly:justify-between aspectly:gap-2">
                              <h3
                                className={`aspectly:font-bold aspectly:text-sm aspectly:truncate ${isSelected ? 'aspectly:text-on-primary-container' : 'aspectly:text-on-surface'}`}
                              >
                                {t.name}
                              </h3>
                              {isCustomFormat && (
                                <span className="aspectly:text-[9px] aspectly:uppercase aspectly:font-black aspectly:tracking-widest aspectly:text-primary aspectly:shrink-0">
                                  Custom
                                </span>
                              )}
                            </div>
                            {t.description ? (
                              <p
                                className={`aspectly:text-xs aspectly:mt-1 aspectly:line-clamp-2 ${isSelected ? 'aspectly:text-on-primary-container/80' : 'aspectly:text-on-surface-variant'}`}
                              >
                                {t.description}
                              </p>
                            ) : null}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="aspectly:col-span-1 aspectly:lg:col-span-7 aspectly:flex aspectly:flex-col aspectly:gap-4">
              <div className="aspectly:bg-surface-container-lowest aspectly:rounded-2xl aspectly:p-6 aspectly:shadow-xs aspectly:flex aspectly:flex-col aspectly:relative aspectly:overflow-hidden aspectly:h-full aspectly:min-h-[400px]">
                {selectedTemplateSummary ? (
                  <>
                    <div className="aspectly:flex aspectly:justify-between aspectly:items-center aspectly:mb-6 aspectly:shrink-0">
                      <div className="aspectly:flex aspectly:flex-col">
                        <h2 className="aspectly:text-xl aspectly:font-extrabold aspectly:font-headline aspectly:text-on-surface">
                          {selectedTemplateSummary.name}
                        </h2>
                        <span className="aspectly:text-xs aspectly:text-on-surface-variant aspectly:font-medium aspectly:mt-1">
                          {selectedTemplateSummary.description}
                        </span>
                      </div>
                    </div>

                    <div className="aspectly:relative aspectly:overflow-hidden aspectly:flex aspectly:items-center aspectly:justify-center aspectly:bg-surface aspectly:rounded-xl aspectly:p-4 aspectly:grow aspectly:min-h-[300px]">
                      <div className="aspectly:relative aspectly:w-full aspectly:max-w-2xl aspectly:mx-auto aspectly:flex aspectly:flex-col aspectly:items-center">
                        <div
                          className="aspectly:relative aspectly:w-full aspectly:bg-[#0b0f14] aspectly:rounded-[1rem] aspectly:p-[1.5%] aspectly:shadow-2xl aspectly:border aspectly:border-black/60 aspectly:overflow-hidden"
                          style={{ aspectRatio: previewAspect }}
                        >
                          {previewTemplate ? (
                            <TemplatePreview template={previewTemplate} />
                          ) : (
                            <div className="aspectly:w-full aspectly:h-full aspectly:bg-linear-to-br aspectly:from-primary-container aspectly:to-surface-container aspectly:rounded-xs aspectly:overflow-hidden aspectly:flex aspectly:items-center aspectly:justify-center">
                              <div className="aspectly:text-center aspectly:space-y-3 aspectly:opacity-40">
                                <span className="material-symbols-outlined aspectly:text-7xl aspectly:text-primary">
                                  tv
                                </span>
                                <p className="aspectly:text-sm aspectly:font-bold aspectly:text-on-surface-variant">
                                  {selectedTemplateSummary.name}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="aspectly:flex aspectly:flex-col aspectly:items-center">
                          <div className="aspectly:w-24 aspectly:md:w-32 aspectly:h-2 aspectly:bg-[#0f141a] aspectly:rounded-b-lg" />
                          <div className="aspectly:w-36 aspectly:md:w-48 aspectly:h-1.5 aspectly:bg-[#11161d] aspectly:rounded-full aspectly:-mt-0.5 aspectly:shadow-lg" />
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="aspectly:grow aspectly:flex aspectly:flex-col aspectly:items-center aspectly:justify-center aspectly:text-center aspectly:p-8 aspectly:opacity-60">
                    <span className="material-symbols-outlined aspectly:text-6xl aspectly:text-on-surface-variant aspectly:mb-4">
                      view_in_ar
                    </span>
                    <h3 className="aspectly:text-lg aspectly:font-bold aspectly:text-on-surface aspectly:font-headline aspectly:mb-2">
                      Select a Template
                    </h3>
                    <p className="aspectly:text-sm aspectly:text-on-surface-variant aspectly:max-w-sm">
                      Choose a template from the list on the left to preview.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
